// Het codeblok, met een voorbeeld eronder bij twee talen: `mermaid` tekent een diagram,
// `math` zet een formule.
//
// Waarom een codeblok en geen eigen soort blok: zo blijft het gewone tekst. In de export staat
// er ```mermaid of ```math met de regels eronder, en dat leest elke markdown-lezer — GitHub
// tekent allebei zelfs. Een eigen blokvorm zou buiten deze app niets betekenen, en import en
// export hoeven er niets extra's voor te weten.
//
// Mermaid en KaTeX worden pas gehaald als er echt zo'n blok op het scherm staat: samen zijn ze
// groter dan de hele app, en de meeste notities hebben geen tekening of formule.

import CodeBlock from "@tiptap/extension-code-block";

let mermaidBelofte: Promise<typeof import("mermaid").default> | null = null;

function haalMermaid(): Promise<typeof import("mermaid").default> {
  if (!mermaidBelofte) {
    mermaidBelofte = import("mermaid").then((mod) => {
      mod.default.initialize({
        startOnLoad: false,
        // `neutral` past bij het papierwit van de app; de standaardkleuren zijn felblauw.
        theme: "neutral",
        securityLevel: "strict",
        fontFamily: "var(--schrift)"
      });
      return mod.default;
    });
  }
  return mermaidBelofte;
}

let katexBelofte: Promise<typeof import("katex").default> | null = null;

function haalKatex(): Promise<typeof import("katex").default> {
  if (!katexBelofte) katexBelofte = import("katex").then((mod) => mod.default);
  return katexBelofte;
}

/** De talen waarbij we iets onder de code laten zien. */
const TEKENTALEN = new Set(["mermaid"]);
const WISKUNDETALEN = new Set(["math", "katex", "latex"]);

let teller = 0;

export const Codeblok = CodeBlock.extend({
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("div");
      dom.className = "codeblokvak";

      const pre = document.createElement("pre");
      const code = document.createElement("code");
      pre.appendChild(code);
      dom.appendChild(pre);

      const tekening = document.createElement("div");
      tekening.className = "diagram";
      tekening.contentEditable = "false";
      dom.appendChild(tekening);

      let klok: number | null = null;
      let laatste = "";

      const teken = (huidig: typeof node) => {
        const taal = String(huidig.attrs.language ?? "").toLowerCase();
        const tekst = huidig.textContent;
        if ((!TEKENTALEN.has(taal) && !WISKUNDETALEN.has(taal)) || tekst.trim() === "") {
          tekening.style.display = "none";
          tekening.innerHTML = "";
          laatste = "";
          return;
        }
        if (tekst === laatste) return;
        laatste = tekst;
        tekening.style.display = "";

        // Niet bij elke toetsaanslag tekenen: mermaid is traag genoeg om dat te voelen.
        if (klok) window.clearTimeout(klok);
        klok = window.setTimeout(async () => {
          try {
            if (WISKUNDETALEN.has(taal)) {
              const katex = await haalKatex();
              tekening.innerHTML = katex.renderToString(tekst, { displayMode: true, throwOnError: true, output: "html" });
            } else {
              const mermaid = await haalMermaid();
              const { svg } = await mermaid.render(`diagram-${++teller}`, tekst);
              tekening.innerHTML = svg;
            }
            tekening.classList.remove("diagramfout");
          } catch (fout) {
            // Een halve tekening tijdens het typen is normaal; laat zien wat er mis is in
            // plaats van een leeg vlak.
            tekening.textContent = (fout as Error).message?.split("\n")[0] ?? "Deze tekening kan ik nog niet lezen.";
            tekening.classList.add("diagramfout");
          }
        }, 500);
      };

      teken(node);

      return {
        dom,
        contentDOM: code,
        update(nieuw) {
          if (nieuw.type.name !== node.type.name) return false;
          teken(nieuw);
          return true;
        },
        destroy() {
          if (klok) window.clearTimeout(klok);
        },
        // De tekening is geen tekst van de editor: klikken erin mag de cursor niet verzetten.
        ignoreMutation(mutatie) {
          return tekening.contains(mutatie.target as Node);
        }
      };
    };
  }
});
